// ── SVG rendering engine for the Lacuna board ──

import type {
  BoardState,
  GameState,
  Strand,
  Vertex,
  PlayerColor,
  LacunaRegion,
} from '../game/state';
import {
  BOARD_RADIUS,
  ANCHOR_COUNT,
  anchorPosition,
  anchorAngle,
  chaikinSmooth,
} from '../board/geometry';
import type { Face } from '../game/topology';
import { enumerateFaces } from '../game/topology';

// ── Strand path cache (populated by renderStrands, consumed by buildLacunaPath) ──
// Stores the exact SVG curve segments for each strand in BOTH directions.
// This ensures lacuna fills trace the exact same curves pixel-perfectly.

let strandPathCache: Map<string, {
  svgSegments: string;        // C commands for forward direction (from → to)
  svgSegmentsReversed: string; // C commands for reverse direction (to → from)
}> = new Map();

// ── Constants ──

const SVG_NS = 'http://www.w3.org/2000/svg';

const COLOR_CIRCLE_STROKE = '#2A2A2A';
const COLOR_ANCHOR_FILL = '#2A2A2A';
const COLOR_ANCHOR_LABEL = '#999';
const COLOR_NODE_FILL = '#fff';
const COLOR_NODE_STROKE = '#2A2A2A';
const COLOR_BLACK_STRAND = '#1A1A1A';
const COLOR_RED_STRAND = '#C0392B';
const COLOR_BLACK_LACUNA = 'rgba(26,26,26,0.12)';
const COLOR_RED_LACUNA = 'rgba(192,57,43,0.12)';

const CIRCLE_STROKE_WIDTH = 0.06;
const ANCHOR_RADIUS = 0.08;
const ANCHOR_LABEL_FONT_SIZE = 0.35;
const ANCHOR_LABEL_RADIUS = 3.5;
const NODE_RADIUS = 0.06;
const NODE_STROKE_WIDTH = 0.05;
const STRAND_STROKE_WIDTH = 0.05;

// ── Helpers ──

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, String(v));
    }
  }
  return el;
}

function strandColor(color: PlayerColor): string {
  return color === 'black' ? COLOR_BLACK_STRAND : COLOR_RED_STRAND;
}

function lacunaFill(color: PlayerColor): string {
  return color === 'black' ? COLOR_BLACK_LACUNA : COLOR_RED_LACUNA;
}

function clearChildren(el: Element): void {
  while (el.lastChild) {
    el.removeChild(el.lastChild);
  }
}

// ── SVG creation ──

export function createBoardSVG(): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: '-4 -4 8 8',
    xmlns: SVG_NS,
  });
  svg.style.width = '100%';
  svg.style.height = '100%';
  svg.style.display = 'block';

  // Create layer groups in back-to-front order
  const layers = [
    'sealed-regions',
    'outer-circle',
    'strands',
    'nodes',
    'anchors',
    'interaction',
  ];
  for (const name of layers) {
    const g = svgEl('g');
    g.classList.add(name);
    svg.appendChild(g);
  }

  return svg;
}

// ── Layer access ──

function getLayer(svg: SVGSVGElement, name: string): SVGGElement {
  const g = svg.querySelector(`g.${name}`);
  if (!g) throw new Error(`Missing SVG layer: ${name}`);
  return g as SVGGElement;
}

// ── Sealed lacuna polygon ──

/**
 * Build a closed SVG path for a lacuna fill by stitching together
 * the exact SVG curve segments from the rendered strands.
 * This guarantees the fill boundary matches the visible strands pixel-perfectly.
 */
function buildLacunaPath(lacuna: LacunaRegion, board: BoardState): string {
  const strandIds = lacuna.boundaryStrands;
  if (strandIds.length === 0) return '';

  const strands = strandIds.map((id) => (board.strands ?? {})[id]).filter(Boolean);
  if (strands.length === 0) return '';

  // Chain strands into order by matching endpoints
  interface ChainEntry {
    strand: Strand;
    reversed: boolean; // whether we traverse this strand backwards
    fromV: Vertex;
  }

  const chain: ChainEntry[] = [];
  const used = new Set<number>();

  const s0 = strands[0];
  used.add(0);
  const s0from = board.vertices[s0.from];
  const s0to = board.vertices[s0.to];
  if (!s0from || !s0to) return '';
  chain.push({ strand: s0, reversed: false, fromV: s0from });
  let tip = s0.to;

  for (let iter = 0; iter < strands.length - 1; iter++) {
    let found = false;
    for (let i = 0; i < strands.length; i++) {
      if (used.has(i)) continue;
      const s = strands[i];
      if (s.from === tip) {
        const fv = board.vertices[s.from];
        if (fv) {
          chain.push({ strand: s, reversed: false, fromV: fv });
          tip = s.to;
          used.add(i);
          found = true;
        }
        break;
      }
      if (s.to === tip) {
        const fv = board.vertices[s.to];
        if (fv) {
          chain.push({ strand: s, reversed: true, fromV: fv });
          tip = s.from;
          used.add(i);
          found = true;
        }
        break;
      }
    }
    if (!found) break;
  }

  if (chain.length < 2) return '';

  // Build the path by stitching exact SVG segments from the cache
  const parts: string[] = [];

  for (let i = 0; i < chain.length; i++) {
    const { strand, reversed, fromV } = chain[i];
    const cached = strandPathCache.get(strand.id);

    if (i === 0) {
      parts.push(`M ${fromV.x} ${-fromV.y}`);
    }

    if (cached) {
      const segs = reversed ? cached.svgSegmentsReversed : cached.svgSegments;
      if (segs) {
        parts.push(segs);
      } else {
        // Fallback: straight line
        const toV = reversed ? board.vertices[strand.from] : board.vertices[strand.to];
        if (toV) parts.push(`L ${toV.x} ${-toV.y}`);
      }
    } else {
      // No cache — straight line fallback
      const toV = reversed ? board.vertices[strand.from] : board.vertices[strand.to];
      if (toV) parts.push(`L ${toV.x} ${-toV.y}`);
    }
  }

  parts.push('Z');
  return parts.join(' ');
}

// ── Render sealed lacunae ──

function renderSealedRegions(
  layer: SVGGElement,
  state: GameState,
): void {
  clearChildren(layer);
  const { board, lacunae } = state;
  if (!lacunae) return;

  for (const lacuna of Object.values(lacunae)) {
    const d = buildLacunaPath(lacuna, board);
    if (!d) continue;

    const path = svgEl('path', {
      d,
      fill: lacunaFill(lacuna.color),
      stroke: 'none',
    });
    layer.appendChild(path);
  }
}

// ── Render outer circle ──

function renderOuterCircle(layer: SVGGElement): void {
  clearChildren(layer);
  const circle = svgEl('circle', {
    cx: 0,
    cy: 0,
    r: BOARD_RADIUS,
    fill: 'none',
    stroke: COLOR_CIRCLE_STROKE,
    'stroke-width': CIRCLE_STROKE_WIDTH,
  });
  layer.appendChild(circle);
}

// ── Render strands ──

/**
 * Sample evenly-spaced points along a polyline of waypoints.
 * Skips the first and last points (those are vertices, already in obstacles).
 */
function sampleWaypoints(
  waypoints: Array<{ x: number; y: number }>,
  count = 4,
): Array<{ x: number; y: number }> {
  if (waypoints.length <= 2) return [];
  const samples: Array<{ x: number; y: number }> = [];

  // Compute total length
  let totalLen = 0;
  for (let i = 1; i < waypoints.length; i++) {
    totalLen += Math.hypot(
      waypoints[i].x - waypoints[i - 1].x,
      waypoints[i].y - waypoints[i - 1].y,
    );
  }
  if (totalLen < 0.01) return [];

  // Walk the polyline, sampling at even intervals (skip endpoints)
  for (let s = 1; s <= count; s++) {
    const targetDist = (s / (count + 1)) * totalLen;
    let walked = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const segLen = Math.hypot(
        waypoints[i].x - waypoints[i - 1].x,
        waypoints[i].y - waypoints[i - 1].y,
      );
      if (walked + segLen >= targetDist) {
        const t = (targetDist - walked) / segLen;
        samples.push({
          x: waypoints[i - 1].x + (waypoints[i].x - waypoints[i - 1].x) * t,
          y: waypoints[i - 1].y + (waypoints[i].y - waypoints[i - 1].y) * t,
        });
        break;
      }
      walked += segLen;
    }
  }
  return samples;
}

function renderStrands(layer: SVGGElement, board: BoardState): void {
  clearChildren(layer);
  if (!board.strands) return;

  // Clear path cache
  strandPathCache = new Map();

  for (const strand of Object.values(board.strands)) {
    const fromV = board.vertices[strand.from];
    const toV = board.vertices[strand.to];
    if (!fromV || !toV) continue;

    // Get waypoints: player-drawn path, or fallback to straight line
    let waypoints: Array<{ x: number; y: number }>;
    if (strand.waypoints && strand.waypoints.length >= 2) {
      waypoints = strand.waypoints;
    } else {
      waypoints = [{ x: fromV.x, y: fromV.y }, { x: toV.x, y: toV.y }];
    }

    // Apply Chaikin smoothing (2 iterations — smooth but stays close to drawn path)
    const smoothed = chaikinSmooth(waypoints, 2);

    // Build SVG path
    const parts: string[] = [`M ${smoothed[0].x} ${-smoothed[0].y}`];
    for (let i = 1; i < smoothed.length; i++) {
      parts.push(`L ${smoothed[i].x} ${-smoothed[i].y}`);
    }
    const fullPath = parts.join(' ');

    const pathEl = svgEl('path', {
      d: fullPath,
      fill: 'none',
      stroke: strandColor(strand.color),
      'stroke-width': STRAND_STROKE_WIDTH,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
    pathEl.dataset.strandId = strand.id;
    layer.appendChild(pathEl);

    // Cache path segments for lacuna fills
    // Forward: everything after the M command
    const firstL = fullPath.indexOf('L');
    const svgSegments = firstL >= 0 ? fullPath.slice(firstL) : '';

    // Reverse: build from reversed smoothed points
    const revSmoothed = [...smoothed].reverse();
    const revParts: string[] = [];
    for (let i = 1; i < revSmoothed.length; i++) {
      revParts.push(`L ${revSmoothed[i].x} ${-revSmoothed[i].y}`);
    }
    const svgSegmentsReversed = revParts.join(' ');

    strandPathCache.set(strand.id, { svgSegments, svgSegmentsReversed });
  }
}

// ── Render nodes ──

function renderNodes(layer: SVGGElement, board: BoardState): void {
  clearChildren(layer);

  for (const vertex of Object.values(board.vertices ?? {})) {
    if (vertex.type !== 'node') continue;

    const circle = svgEl('circle', {
      cx: vertex.x,
      cy: -vertex.y,
      r: NODE_RADIUS,
      fill: COLOR_NODE_FILL,
      stroke: COLOR_NODE_STROKE,
      'stroke-width': NODE_STROKE_WIDTH,
    });
    circle.dataset.vertexId = vertex.id;
    layer.appendChild(circle);

    // Socket count badge (only if node has free sockets)
    if (vertex.free > 0) {
      const badge = svgEl('text', {
        x: vertex.x + 0.15,
        y: -vertex.y + 0.05,
        'text-anchor': 'start',
        'dominant-baseline': 'central',
        fill: '#888',
        'font-size': 0.18,
        'font-family': 'system-ui, sans-serif',
      });
      badge.textContent = String(vertex.free);
      badge.style.pointerEvents = 'none';
      layer.appendChild(badge);
    }
  }
}

// ── Render anchors ──

function renderAnchors(layer: SVGGElement, board: BoardState): void {
  clearChildren(layer);

  for (let i = 1; i <= ANCHOR_COUNT; i++) {
    const pos = anchorPosition(i);
    const vid = `A${i}`;
    const vertex = board.vertices[vid];
    const free = vertex?.free ?? 2;

    // Anchor dot
    const dot = svgEl('circle', {
      cx: pos.x,
      cy: -pos.y,
      r: ANCHOR_RADIUS,
      fill: COLOR_ANCHOR_FILL,
    });
    dot.dataset.vertexId = vid;
    layer.appendChild(dot);

    // Anchor label positioned just outside the circle
    const angle = anchorAngle(i);
    const lx = ANCHOR_LABEL_RADIUS * Math.cos(angle);
    const ly = ANCHOR_LABEL_RADIUS * Math.sin(angle);
    const label = svgEl('text', {
      x: lx,
      y: -ly,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
      fill: COLOR_ANCHOR_LABEL,
      'font-size': ANCHOR_LABEL_FONT_SIZE,
      'font-family': 'system-ui, sans-serif',
    });
    label.textContent = String(i);
    layer.appendChild(label);

    // Socket count — small pip(s) inside the anchor label area
    if (free < 2) {
      const sockLabel = svgEl('text', {
        x: lx,
        y: -ly + 0.28,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        fill: free === 0 ? '#ccc' : '#888',
        'font-size': 0.2,
        'font-family': 'system-ui, sans-serif',
      });
      sockLabel.textContent = free === 0 ? '\u2013' : `${free}`;
      sockLabel.style.pointerEvents = 'none';
      layer.appendChild(sockLabel);
    }
  }
}

// ── Render interaction layer (transparent hit targets) ──

function renderInteraction(
  layer: SVGGElement,
  board: BoardState,
): void {
  clearChildren(layer);

  // Hit targets for each vertex (anchors + nodes)
  for (const vertex of Object.values(board.vertices ?? {})) {
    const hitTarget = svgEl('circle', {
      cx: vertex.x,
      cy: -vertex.y,
      r: vertex.type === 'anchor' ? 0.2 : 0.15,
      fill: 'transparent',
      stroke: 'none',
      cursor: 'pointer',
    });
    hitTarget.dataset.vertexId = vertex.id;
    hitTarget.classList.add('hit-target');
    layer.appendChild(hitTarget);
  }

  // Full-board hit area for node placement (behind vertex targets)
  const boardHit = svgEl('circle', {
    cx: 0,
    cy: 0,
    r: BOARD_RADIUS,
    fill: 'transparent',
    stroke: 'none',
    cursor: 'crosshair',
  });
  boardHit.classList.add('board-hit');
  // Insert at the beginning so vertex targets sit on top
  layer.insertBefore(boardHit, layer.firstChild);
}

// ── Public API ──

export function renderBoard(svg: SVGSVGElement, state: GameState): void {
  // Strands must render first to populate the waypoint cache
  // that buildLacunaPath uses for matched fill curves
  renderStrands(getLayer(svg, 'strands'), state.board);
  renderSealedRegions(getLayer(svg, 'sealed-regions'), state);
  renderOuterCircle(getLayer(svg, 'outer-circle'));
  renderNodes(getLayer(svg, 'nodes'), state.board);
  renderAnchors(getLayer(svg, 'anchors'), state.board);
  renderInteraction(getLayer(svg, 'interaction'), state.board);
}

export function highlightVertices(
  svg: SVGSVGElement,
  vertexIds: string[],
  board: BoardState,
): void {
  clearHighlights(svg);
  const layer = getLayer(svg, 'interaction');

  for (const id of vertexIds) {
    const vertex = board.vertices[id];
    if (!vertex) continue;

    const ring = svgEl('circle', {
      cx: vertex.x,
      cy: -vertex.y,
      r: vertex.type === 'anchor' ? 0.18 : 0.14,
      fill: 'none',
      stroke: '#4A90D9',
      'stroke-width': 0.04,
    });
    ring.classList.add('highlight-ring');
    layer.appendChild(ring);
  }
}

export function clearHighlights(svg: SVGSVGElement): void {
  const layer = getLayer(svg, 'interaction');
  const rings = layer.querySelectorAll('.highlight-ring');
  rings.forEach((r) => r.remove());
}

export function showNodePreview(
  svg: SVGSVGElement,
  x: number,
  y: number,
): void {
  hideNodePreview(svg);
  const layer = getLayer(svg, 'interaction');

  const preview = svgEl('circle', {
    cx: x,
    cy: -y,
    r: NODE_RADIUS,
    fill: COLOR_NODE_FILL,
    stroke: COLOR_NODE_STROKE,
    'stroke-width': NODE_STROKE_WIDTH,
    opacity: 0.5,
  });
  preview.classList.add('node-preview');
  preview.style.pointerEvents = 'none';
  layer.appendChild(preview);
}

export function hideNodePreview(svg: SVGSVGElement): void {
  const layer = getLayer(svg, 'interaction');
  const existing = layer.querySelector('.node-preview');
  if (existing) existing.remove();
}


export function clearStrandPreviews(svg: SVGSVGElement): void {
  const layer = getLayer(svg, 'interaction');
  const previews = layer.querySelectorAll('.strand-preview, .live-polyline, .completed-strand-preview');
  previews.forEach((p) => p.remove());
}

/**
 * Draw all completed strands from the current turn as solid (non-dashed) previews.
 * These persist while the player draws additional strands.
 */
export function drawCompletedStrands(
  svg: SVGSVGElement,
  strands: Array<{ waypoints: Array<{ x: number; y: number }> }>,
  color: PlayerColor,
): void {
  const layer = getLayer(svg, 'interaction');

  // Remove old completed strand previews
  layer.querySelectorAll('.completed-strand-preview').forEach(p => p.remove());

  for (const strand of strands) {
    if (strand.waypoints.length < 2) continue;

    const smoothed = chaikinSmooth(strand.waypoints, 2);
    const parts: string[] = [`M ${smoothed[0].x} ${-smoothed[0].y}`];
    for (let i = 1; i < smoothed.length; i++) {
      parts.push(`L ${smoothed[i].x} ${-smoothed[i].y}`);
    }

    const path = svgEl('path', {
      d: parts.join(' '),
      fill: 'none',
      stroke: strandColor(color),
      'stroke-width': STRAND_STROKE_WIDTH,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      opacity: '0.85',
    });
    path.classList.add('completed-strand-preview');
    path.style.pointerEvents = 'none';
    layer.appendChild(path);
  }
}

/**
 * Draw a live polyline (the path the player is currently drawing).
 * Confirmed waypoints are always solid. If `cursorPos` is provided,
 * a dashed extension is drawn from the last waypoint to the cursor.
 */
export function drawLivePolyline(
  svg: SVGSVGElement,
  confirmedWaypoints: Array<{ x: number; y: number }>,
  color: string,
  cursorPos: { x: number; y: number } | null,
): void {
  const layer = getLayer(svg, 'interaction');

  // Remove previous live polyline elements
  layer.querySelectorAll('.live-polyline').forEach(p => p.remove());

  // 1. Draw the confirmed path as solid
  if (confirmedWaypoints.length >= 2) {
    const smoothed = chaikinSmooth(confirmedWaypoints, 2);
    const parts: string[] = [`M ${smoothed[0].x} ${-smoothed[0].y}`];
    for (let i = 1; i < smoothed.length; i++) {
      parts.push(`L ${smoothed[i].x} ${-smoothed[i].y}`);
    }

    const solidPath = svgEl('path', {
      d: parts.join(' '),
      fill: 'none',
      stroke: color,
      'stroke-width': STRAND_STROKE_WIDTH,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      opacity: '0.7',
    });
    solidPath.classList.add('live-polyline');
    solidPath.style.pointerEvents = 'none';
    layer.appendChild(solidPath);
  }

  // 2. Draw the dashed extension from last waypoint to cursor
  if (cursorPos && confirmedWaypoints.length >= 1) {
    const last = confirmedWaypoints[confirmedWaypoints.length - 1];
    const dashPath = svgEl('line', {
      x1: last.x,
      y1: -last.y,
      x2: cursorPos.x,
      y2: -cursorPos.y,
      stroke: color,
      'stroke-width': STRAND_STROKE_WIDTH,
      'stroke-linecap': 'round',
      'stroke-dasharray': '0.1 0.08',
      opacity: '0.4',
    });
    dashPath.classList.add('live-polyline');
    dashPath.style.pointerEvents = 'none';
    layer.appendChild(dashPath);
  }
}

